import { useState } from 'react';
import { Avatar, Badge, Button, Input, Alert, AlertDescription, Spinner } from '@klaro7/ui';

import {
  useAdmins,
  useCreateAdmin,
  useUpdateAdminRole,
  useChangeAdminPassword,
} from '@/features/admins/hooks/useAdmins';
import type { AdminUser } from '@/features/admins/services/admins.service';

/* ─────────────────────── Create Admin Modal ─────────────────────── */

function CreateAdminModal({ onClose }: { onClose: () => void }) {
  const createMutation = useCreateAdmin();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    role: 'admin' as 'admin' | 'super_admin',
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCreate() {
    const errors: string[] = [];
    if (!form.firstName.trim()) errors.push('Nome obbligatorio');
    if (!form.lastName.trim()) errors.push('Cognome obbligatorio');
    if (!form.email.trim()) errors.push('Email obbligatoria');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.push('Email non valida');
    if (!form.password) {
      errors.push('Password obbligatoria');
    } else {
      if (form.password.length < 8) errors.push('Password: almeno 8 caratteri');
      if (!/[a-z]/.test(form.password)) errors.push('Password: almeno una lettera minuscola');
      if (!/[A-Z]/.test(form.password)) errors.push('Password: almeno una lettera maiuscola');
      if (!/[0-9]/.test(form.password)) errors.push('Password: almeno un numero');
    }
    if (form.phone && (form.phone.length < 10 || form.phone.length > 20)) {
      errors.push('Telefono: tra 10 e 20 caratteri');
    }
    if (errors.length > 0) {
      setAlert({ type: 'error', message: errors.join(' · ') });
      return;
    }
    try {
      await createMutation.mutateAsync({
        email: form.email,
        password: form.password,
        role: form.role,
        profile: {
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone || undefined,
        },
      });
      onClose();
    } catch (err: unknown) {
      let msg = 'Errore nella creazione';
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = err as { response?: { data?: { error?: { message?: string; details?: Array<{ message: string }> } } } };
        const details = resp.response?.data?.error?.details;
        if (details?.length) {
          msg = details.map((d) => d.message).join(' · ');
        } else {
          msg = resp.response?.data?.error?.message || msg;
        }
      }
      setAlert({ type: 'error', message: msg });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="relative mx-4 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-6 py-4">
          <h3 className="font-semibold text-neutral-900">Nuovo Amministratore</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 p-6">
          {alert && (
            <Alert variant={alert.type === 'success' ? 'success' : 'error'}>
              <AlertDescription>{alert.message}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nome *"
              value={form.firstName}
              onChange={(e) => update('firstName', e.target.value)}
              placeholder="Mario"
            />
            <Input
              label="Cognome *"
              value={form.lastName}
              onChange={(e) => update('lastName', e.target.value)}
              placeholder="Rossi"
            />
          </div>
          <Input
            label="Email *"
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="admin@klaro7.com"
          />
          <Input
            label="Password *"
            type="password"
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
            placeholder="Min. 8 caratteri, maiuscola, minuscola, numero"
          />
          <Input
            label="Telefono"
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="+39 333 1234567"
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Ruolo *</label>
            <div className="flex gap-3">
              <label
                className={`flex flex-1 cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-all ${
                  form.role === 'admin'
                    ? 'border-brand-teal bg-brand-teal-50/50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={form.role === 'admin'}
                  onChange={() => update('role', 'admin')}
                  className="h-4 w-4 border-neutral-300 text-brand-teal focus:ring-brand-teal"
                />
                <div>
                  <p className="text-sm font-medium text-neutral-900">Admin</p>
                  <p className="text-xs text-neutral-600">Accesso amministrativo</p>
                </div>
              </label>
              <label
                className={`flex flex-1 cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-all ${
                  form.role === 'super_admin'
                    ? 'border-brand-teal bg-brand-teal-50/50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  value="super_admin"
                  checked={form.role === 'super_admin'}
                  onChange={() => update('role', 'super_admin')}
                  className="h-4 w-4 border-neutral-300 text-brand-teal focus:ring-brand-teal"
                />
                <div>
                  <p className="text-sm font-medium text-neutral-900">Super Admin</p>
                  <p className="text-xs text-neutral-600">Accesso completo al sistema</p>
                </div>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-neutral-100 pt-4">
            <Button variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button onClick={handleCreate} loading={createMutation.isPending}>
              Crea Amministratore
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Admin Detail Modal ─────────────────────── */

function AdminDetailPanel({
  admin,
  onClose,
}: {
  admin: AdminUser;
  onClose: () => void;
}) {
  const roleMutation = useUpdateAdminRole();
  const passwordMutation = useChangeAdminPassword();
  const [newPassword, setNewPassword] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fullName = `${admin.profile?.firstName ?? ''} ${admin.profile?.lastName ?? ''}`.trim() || admin.email;
  const isSuperAdmin = admin.role === 'super_admin';

  async function handleToggleRole() {
    const newRole = isSuperAdmin ? 'admin' : 'super_admin';
    try {
      await roleMutation.mutateAsync({ id: admin.id, role: newRole });
      setAlert({ type: 'success', message: `Ruolo aggiornato a ${newRole === 'super_admin' ? 'Super Admin' : 'Admin'}` });
    } catch {
      setAlert({ type: 'error', message: 'Errore nel cambio ruolo' });
    }
  }

  async function handleChangePassword() {
    const errors: string[] = [];
    if (newPassword.length < 8) errors.push('Almeno 8 caratteri');
    if (!/[a-z]/.test(newPassword)) errors.push('Almeno una minuscola');
    if (!/[A-Z]/.test(newPassword)) errors.push('Almeno una maiuscola');
    if (!/[0-9]/.test(newPassword)) errors.push('Almeno un numero');
    if (errors.length > 0) {
      setAlert({ type: 'error', message: `Password: ${errors.join(' · ')}` });
      return;
    }
    try {
      await passwordMutation.mutateAsync({ id: admin.id, password: newPassword });
      setNewPassword('');
      setAlert({ type: 'success', message: 'Password aggiornata con successo' });
    } catch {
      setAlert({ type: 'error', message: 'Errore nel cambio password' });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="relative mx-4 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <Avatar fallback={fullName} size="md" />
            <div>
              <h3 className="font-semibold text-neutral-900">{fullName}</h3>
              <Badge variant={isSuperAdmin ? 'error' : 'info'} className="mt-0.5">
                {isSuperAdmin ? 'Super Admin' : 'Admin'}
              </Badge>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 p-6">
          {alert && (
            <Alert variant={alert.type === 'success' ? 'success' : 'error'}>
              <AlertDescription>{alert.message}</AlertDescription>
            </Alert>
          )}

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-neutral-600">Email</p>
              <p className="mt-1 text-sm font-medium text-neutral-900">{admin.email}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-neutral-600">Stato</p>
              <Badge variant={admin.status === 'active' ? 'success' : 'default'} className="mt-1">
                {admin.status === 'active' ? 'Attivo' : admin.status}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-neutral-600">Creato il</p>
              <p className="mt-1 text-sm text-neutral-700">
                {new Date(admin.createdAt).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-neutral-600">Ultimo aggiornamento</p>
              <p className="mt-1 text-sm text-neutral-700">
                {new Date(admin.updatedAt).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>

          {/* Change password */}
          <div className="border-t border-neutral-100 pt-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-neutral-600">Cambia Password</p>
            <div className="flex gap-2">
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nuova password (min. 8 caratteri)"
              />
              <Button
                onClick={handleChangePassword}
                loading={passwordMutation.isPending}
                disabled={!newPassword}
                size="md"
              >
                Cambia
              </Button>
            </div>
          </div>

          {/* Role toggle */}
          <div className="border-t border-neutral-100 pt-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-neutral-600">Gestione Ruolo</p>
            <Button
              variant="outline"
              onClick={handleToggleRole}
              loading={roleMutation.isPending}
              className="w-full"
            >
              {isSuperAdmin ? 'Declassa ad Admin' : 'Promuovi a Super Admin'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Main Page ─────────────────────── */

export function AdminAdminsPage() {
  const { data: admins, isLoading } = useAdmins();
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const superAdmins = admins?.filter((a) => a.role === 'super_admin') || [];
  const regularAdmins = admins?.filter((a) => a.role === 'admin') || [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Gestione Amministratori</h1>
          <p className="mt-1 text-sm text-neutral-600">Visualizza e gestisci gli amministratori della piattaforma</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Nuovo Amministratore
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-neutral-200 bg-white p-5 border-l-4 border-l-brand-teal">
          <p className="text-sm text-neutral-600">Totale</p>
          <p className="mt-1 text-2xl font-bold text-neutral-900">{admins?.length || 0}</p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-5 border-l-4 border-l-red-400">
          <p className="text-sm text-neutral-600">Super Admin</p>
          <p className="mt-1 text-2xl font-bold text-neutral-900">{superAdmins.length}</p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-5 border-l-4 border-l-blue-400">
          <p className="text-sm text-neutral-600">Admin</p>
          <p className="mt-1 text-2xl font-bold text-neutral-900">{regularAdmins.length}</p>
        </div>
      </div>

      {/* Admin table */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Amministratori</h3>
            <p className="text-xs text-neutral-600">Clicca su un amministratore per gestirlo</p>
          </div>
        </div>

        {(!admins || admins.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
              <svg className="h-6 w-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <p className="mt-4 font-medium text-neutral-700">Nessun amministratore trovato</p>
            <p className="mt-1 text-sm text-neutral-600">Crea il primo amministratore</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50/50">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Amministratore</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Email</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Ruolo</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Stato</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Creato il</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {admins.map((admin) => (
                  <tr
                    key={admin.id}
                    onClick={() => setSelectedAdmin(admin)}
                    className="cursor-pointer hover:bg-neutral-50/50 transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar fallback={`${admin.profile?.firstName ?? ''} ${admin.profile?.lastName ?? ''}`.trim() || admin.email} size="sm" />
                        <span className="font-medium text-neutral-900">
                          {`${admin.profile?.firstName ?? ''} ${admin.profile?.lastName ?? ''}`.trim() || admin.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-neutral-600">{admin.email}</td>
                    <td className="px-5 py-3.5">
                      <Badge variant={admin.role === 'super_admin' ? 'error' : 'info'}>
                        {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge variant={admin.status === 'active' ? 'success' : 'default'}>
                        {admin.status === 'active' ? 'Attivo' : admin.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-neutral-600">
                      {new Date(admin.createdAt).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedAdmin && (
        <AdminDetailPanel
          admin={selectedAdmin}
          onClose={() => setSelectedAdmin(null)}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateAdminModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}
